/**
 * Names a session's device for Settings › Devices: the browser and the
 * operating system ("Chrome" on "Windows"), so a session you don't
 * recognise stands out. Live bug 2026-10-28: every row was just "Win32"
 * (navigator.platform). The user agent is read here, on the device; the
 * server stores what the client sends.
 */

export interface DeviceName {
  /** The browser, or null when it can't be told. */
  browser: string | null;
  /** The operating system, or null when it can't be told. */
  os: string | null;
}

/** Brands of `navigator.userAgentData` that are not a browser's name. */
const GENERIC_BRANDS = /^(Chromium|Not.?A.?Brand)$/i;

export function deviceName(
  ua: string,
  platform = '',
  brands: ReadonlyArray<{ brand: string }> = [],
): DeviceName {
  const brand = brands.map((b) => b.brand).find((b) => !GENERIC_BRANDS.test(b));
  let browser: string | null = null;
  if (/\bEdg(e|A|iOS)?\//.test(ua)) browser = 'Edge';
  else if (/\bOPR\/|\bOpera\b/.test(ua)) browser = 'Opera';
  else if (/\bVivaldi\//.test(ua)) browser = 'Vivaldi';
  else if (/\bYaBrowser\//.test(ua)) browser = 'Yandex Browser';
  else if (/\bSamsungBrowser\//.test(ua)) browser = 'Samsung Internet';
  else if (/\bFirefox\/|\bFxiOS\//.test(ua)) browser = 'Firefox';
  // Chrome (headless too), or a Chromium browser that names itself only in the brands (Brave)
  else if (/Chrome\/|\bCriOS\//.test(ua))
    browser = brand && !/Chrome/.test(brand) ? brand : 'Chrome';
  else if (/\bVersion\/[\d.]+.*\bSafari\//.test(ua)) browser = 'Safari';
  else if (brand) browser = brand.replace(/^Google /, '');
  return { browser, os: osName(`${platform} ${ua}`) };
}

/**
 * A readable operating system from a platform or user-agent string, also
 * for sessions saved before devices were named ("Win32" → "Windows").
 */
export function osName(s: string | null | undefined): string | null {
  if (!s) return null;
  if (/Windows|Win32|Win64|^Win/i.test(s)) return 'Windows';
  if (/Android/i.test(s)) return 'Android';
  if (/iPhone|iPad|iPod|iOS/i.test(s)) return 'iOS';
  if (/Mac/i.test(s)) return 'macOS';
  if (/CrOS/i.test(s)) return 'ChromeOS';
  if (/Linux|X11/i.test(s)) return 'Linux';
  return s.trim() || null;
}

/** This device, as the sign-in and sign-up requests describe it. */
export function thisDevice(): DeviceName {
  if (typeof navigator === 'undefined') return { browser: null, os: null };
  const nav = navigator as Navigator & {
    userAgentData?: { brands?: Array<{ brand: string }>; platform?: string };
  };
  return deviceName(
    nav.userAgent ?? '',
    nav.userAgentData?.platform || nav.platform || '',
    nav.userAgentData?.brands ?? [],
  );
}

/** An iPhone or iPad (every browser there is WebKit; iPadOS says "Macintosh" with touch). */
export function isIOSWebKit(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent ?? '';
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}
